/* eslint-disable @typescript-eslint/no-explicit-any */
import {ROWS_PER_PAGE_LIMIT} from '../constants';

// Transform individual benefit data to match table expectations
export const transformBenefitForTable = (benefit: any) => {
  return {
    id: benefit.id,
    name: benefit.name,
    isActionRequired: benefit?.is_action_required,

    isLinkedToOrganisation: benefit.is_linked_to_organisations,
    logo: benefit.photo || benefit.image || benefit.logo || null, // Map various image properties to logo for ContentCard
    photo: benefit.photo || benefit.image || benefit.logo || null, // Keep photo for backward compatibility
    category: benefit.category || benefit.category_name || '',
    description: benefit.description || '',
    organisation_id: benefit.organisation_id, // Preserve organisation_id for navigation
    organisation_name: benefit.organisation_name, // Preserve organisation_name for display
    organisations: benefit?.organisations,
    is_multiple_organisation: benefit?.is_multiple_organisation,
    is_deactivated_by_merchant: benefit?.is_deactivated_by_merchant,
    discount: (() => {
      // Handle direct discount fields on benefit object
      if (benefit.discount_fixed_amount) {
        return `${benefit.discount_fixed_amount} kr`;
      }
      if (benefit.discount_percentage) {
        return `${benefit.discount_percentage}%`;
      }
      // Handle nested discount info structure (fallback)
      if (benefit.discountBenefitInfo) {
        const {discount_fixed_amount, discount_percentage} =
          benefit.discountBenefitInfo;
        if (discount_fixed_amount) {
          return discount_fixed_amount.toString();
        }
        if (discount_percentage) {
          return `${discount_percentage}%`;
        }
      }
      // Final fallback to other fields
      return (
        benefit.discount || benefit.discount_amount || benefit.amount || ''
      );
    })(),
    benefitRevenueType: benefit.benefit_revenue_type || null,
    cap: benefit?.commission_cap_type
      ? {
          type: benefit?.commission_cap_type,
          percentage: benefit?.commission_cap_percentage,
          amount: benefit?.commission_cap_fixed_amount
        }
      : null,
    expires: benefit?.ends_at || benefit.expires || benefit.expires_at || null,
    starts: benefit?.starts_at,
    isPublic: benefit.isPublic || benefit.is_public || false,
    isToggled: benefit.status === 'active' || false,
    status: benefit.status || {
      text: benefit.status_text || 'active',
      type: benefit.status_type || 'active'
    }
  };
};

const extractBenefitsArray = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.benefits)) return response.benefits;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const extractPagination = (response: any): any => {
  // Check all possible locations for pagination metadata
  if (!response || Array.isArray(response)) return {};

  return (
    response.pagination ||
    response.data?.pagination ||
    response.meta ||
    response.metadata ||
    {}
  );
};

export const getBenefitsResponseTransformer = (response: any) => {
  const benefits = extractBenefitsArray(response);
  const pagination = extractPagination(response);

  const totalItems =
    pagination?.total ??
    pagination?.totalItems ??
    pagination?.total_items ??
    benefits.length;

  const limit = pagination?.limit ?? ROWS_PER_PAGE_LIMIT;
  const totalPages =
    pagination?.total_pages ??
    pagination?.totalPages ??
    (totalItems > 0 ? Math.ceil(totalItems / limit) : 1);

  return {
    benefits: benefits.map(transformBenefitForTable),
    pagination: {
      totalItems,
      totalPages,
      currentPage:
        pagination?.current_page ??
        pagination?.currentPage ??
        pagination?.page ??
        1,
      limit,
      hasMore: pagination?.has_more ?? pagination?.hasMore ?? false
    }
  };
};
